import * as analytics from 'analytics/Analytics';
import * as Analytics from 'analytics/Analytics';
import {assert} from 'Asserts';
import {
  ActionOrigin,
  ELN_ANALYTICS_ACTIONS,
  LEGACY_WORKFLOWS_ANALYTICS_ACTIONS,
} from 'constants/AnalyticsConstants';
import ModelTypeConstants from 'constants/ModelTypeConstants';
// @ts-expect-error
// eslint-disable-next-line unused-imports/no-unused-imports
import * as context from 'Context';
import {PlateSchemaType} from 'graphql-client/__generated/graphqlGlobalTypes';
import {isInaccessible} from 'graphql-client/InaccessibleInfo';
import {callSampleStatusWarningModal} from 'inventory/sample-status/SampleStatusWarningModal';
import {stubbableFunctions as destinationPlateStubbableFunctions} from 'notebook/integrations/samples/transfer-table-columns/DestinationMatrixPlateCustomEval';
import {ACTION_TRANSFER} from 'notebook/store/VersioningConstants';
import {
  ActionResultType,
  DoPerformAction,
  performServerSideWriteAction,
} from 'notebook/structured-tables/action-functions/PerformActionFunctions';
import {stubbableFunctions as fetchGridStubbableFunctions} from 'notebook/transfer-tables/fetchGridAndValidateContainers';
import {
  TransferOperation,
  TransferOperationList,
  TransferTableModel,
} from 'notebook/transfer-tables/TransferTableConstants';
import * as TransferTableDAO from 'notebook/transfer-tables/TransferTableDAO';
import {getStore} from 'redux-stores/Store';
import * as SampleUtil from 'samples/SampleUtil';
import * as LoadingScreen from 'ui-library/LoadingScreen';
import APIUtil from 'util/APIUtil';
import * as StageRunActions from 'workflows/StageRunActions';

type CreateTransferTableParams = TransferTableDAO.CreateTransferTableParams;
type TransferSamplesParams = TransferTableDAO.TransferSamplesParams;

export enum TransferTableActionType {
  TRANSFER_OPERATION_LISTS_FETCH_COMPLETED = 'TRANSFER_OPERATION_LISTS_FETCH_COMPLETED',
}

export interface TransferTableAction {
  type: TransferTableActionType.TRANSFER_OPERATION_LISTS_FETCH_COMPLETED;
  transferOperationListsById: {[id: string]: TransferOperationList};
  transferOperationsById: {[id: string]: TransferOperation};
}

export function transferOperationListsFetchCompleted({
  transferOperationListsById,
  transferOperationsById,
}: {
  transferOperationListsById: {[id: string]: TransferOperationList};
  transferOperationsById: {[id: string]: TransferOperation};
}): TransferTableAction {
  return {
    type: TransferTableActionType.TRANSFER_OPERATION_LISTS_FETCH_COMPLETED,
    transferOperationListsById,
    transferOperationsById,
  };
}

/**
 * Make an empty TransferTable in the database so that we can hook up a NotePart to it.
 */
export async function createTransferTable({
  name,
  containerSchemaId,
  parentStorableIsGrid,
  entryId,
}: CreateTransferTableParams): Promise<TransferTableModel> {
  LoadingScreen.show();
  try {
    const {transferTable, transferOperationList} = await TransferTableDAO.createTransferTable({
      name,
      containerSchemaId,
      parentStorableIsGrid,
      entryId,
    });
    const entryType = APIUtil.apiIdToType(entryId);
    if (entryType === ModelTypeConstants.STAGE_ENTRY) {
      analytics.deprecatedTrackEvent(LEGACY_WORKFLOWS_ANALYTICS_ACTIONS.CREATED_TRANSFER_TABLE_IN_ENTRY);
    } else {
      analytics.deprecatedTrackEvent(ELN_ANALYTICS_ACTIONS.CREATED_TRANSFER_TABLE_IN_NOTEBOOK_ENTRY);
    }
    getStore().dispatch(
      transferOperationListsFetchCompleted({
        transferOperationListsById: {[transferOperationList.id]: transferOperationList},
        transferOperationsById: {},
      })
    );
    return transferTable;
  } finally {
    LoadingScreen.hide();
  }
}

async function getSampleIdsForStatusWarningFromLegacyTransferTable({
  operations,
}: Pick<TransferSamplesParams, 'operations'>): Promise<Array<string>> {
  const ids = await Promise.all(
    operations.map(async (operation) => {
      const storableIds: Array<string> = [];
      if (operation.sourceContainerId != null) {
        // TODO(BNCH-46802): Handle containers off of well plates
        storableIds.push(operation.sourceContainerId);
      }

      let isFixedPlate = false;
      if (APIUtil.isPlate(operation.parentStorableData.parentStorableId)) {
        const data = await destinationPlateStubbableFunctions.doQuery(
          operation.parentStorableData.parentStorableId
        );
        const plate = data.storables[0];
        if (isInaccessible(plate)) {
          return storableIds;
        }
        assert(plate.__typename === 'PlateNode', 'Expected a plate');
        assert(plate.storableSchema.__typename === 'PlateSchemaNode', 'Expected a plate schema');
        isFixedPlate = plate.storableSchema.plateSchemaType === PlateSchemaType.FIXED_PLATE;
      }

      if (isFixedPlate) {
        storableIds.push(operation.parentStorableData.parentStorableId);
      } else if (SampleUtil.isGridId(operation.parentStorableData.parentStorableId)) {
        const data = await fetchGridStubbableFunctions.getGridWithContainers(
          operation.parentStorableData.parentStorableId
        );
        if (isInaccessible(data.storable)) {
          return storableIds;
        }
        const grid = data.storable;
        assert(grid.__typename === 'PlateNode' || grid.__typename === 'BoxNode', 'Expected a plate or box');
        operation.parentStorableData.positions?.forEach(({rowIndex, columnIndex}) => {
          const container = grid.cells[rowIndex][columnIndex]?.container;
          if (container != null && !isInaccessible(container)) {
            storableIds.push(container.id);
          }
        });
      }

      return storableIds;
    })
  );
  return ids.flat();
}

export async function transferSamples({
  entryId,
  transferTableProvenanceUuid,
  containerSchemaId,
  operations,
}: TransferSamplesParams): Promise<void> {
  const sampleIds = await getSampleIdsForStatusWarningFromLegacyTransferTable({operations});
  const {shouldProceed} = await callSampleStatusWarningModal({
    sampleIds: [...new Set(sampleIds)],
    trackingParams: {
      actionOrigin: ActionOrigin.NOTEBOOK_ENTRY_LEGACY_TRANSFER_TABLE,
      actionAttempted: 'transfer',
      eventGroupId: Analytics.makeAnalyticsEventId(),
    },
    canSelectMultiple: false,
    showOnlyWarnings: true,
  });

  if (shouldProceed) {
    const entryType = APIUtil.apiIdToType(entryId);
    const doTransferSamples: DoPerformAction<null> = async () => {
      const response = await TransferTableDAO.transferSamples({
        entryId,
        transferTableProvenanceUuid,
        containerSchemaId,
        operations,
      });
      if (entryType === ModelTypeConstants.STAGE_ENTRY) {
        StageRunActions.dispatchStageRunSamplesFetched(response);
      }
      return {resultType: ActionResultType.SUCCESS, responseData: null};
    };
    await performServerSideWriteAction({
      entryId,
      loadingScreenMessage: 'Transferring...',
      autoSaverAction: ACTION_TRANSFER,
      doPerformAction: doTransferSamples,
      analyticsActionForPerfTracking: 'transfer_table_transfer',
    });
  }
}
